package de.oevbs.kunde.kundenportal2.services.contract

import de.oevbs.businesspartner.bpcrmservice2.common.model.LineOfBusiness
import de.oevbs.businesspartner.bpcrmservice2.contract.model.ContractDetails
import de.oevbs.businesspartner.bpcrmservice2.contract.model.Participant
import de.oevbs.businesspartner.bpcrmservice2.contract.model.PartnerRole
import de.oevbs.businesspartner.bpcrmservice2.contract.model.lineofbusiness.*
import de.oevbs.businesspartner.bpcrmservice2.contract.model.pm.PMClause
import de.oevbs.businesspartner.bpcrmservice2.partner.model.toView
import de.oevbs.kosmos.ui.elements.button.btn
import de.oevbs.kosmos.ui.icons.Icon
import de.oevbs.kosmos.ui.icons.MaterialIconsOutlined
import de.oevbs.kosmos.ui.icons.icon
import de.oevbs.kunde.kundenportal2.Pages.myDataContact
import de.oevbs.kunde.kundenportal2.Pages.serviceCarInsuranceCard
import de.oevbs.kunde.kundenportal2.Pages.serviceCarInsuranceDeregister
import de.oevbs.kunde.kundenportal2.Pages.serviceCarInsuranceDigiCard
import de.oevbs.kunde.kundenportal2.Pages.serviceCarTaxCertificates
import de.oevbs.kunde.kundenportal2.Pages.serviceLifeEntryNotification
import de.oevbs.kunde.kundenportal2.components.*
import de.oevbs.kunde.kundenportal2.navigation.NavigationTarget
import de.oevbs.kunde.kundenportal2.navigation.Page
import de.oevbs.kunde.kundenportal2.services.contract.lineofbusiness.*
import de.oevbs.sfa.data.time.toLocalizedString
import dev.fritz2.core.RenderContext
import dev.fritz2.core.invoke
import dev.fritz2.core.joinClasses
import kotlinx.coroutines.flow.combine


fun RenderContext.initAndRenderDetails(contractNumber: String, contractStore: ContractStore) {
    DetailsStore.loadDetails(contractNumber)
    globalInitTracker.data.combine(contractStore.initFailedFlow, ::Pair).render { (loading, failed) ->
        if (loading) return@render
        when {
            failed -> noContractDataFoundMessage()
            else -> contractDetails(contractNumber, contractStore)
        }
    }
}

private fun RenderContext.contractDetails(contractNumber: String, contractStore: ContractStore) {
    DetailsStore.noneNullData.render { details ->
        details.lineOfBusinessData?.let {
            div("w-full p-0 justify-center md:justify-between flex flex-col-reverse md:flex-row gap-y-5") {
                contractTitle(contractNumber, it.lineOfBusiness, details.general.status)
                btn {
                    label("Zurück zur Übersicht")
                    icon(MaterialIconsOutlined.arrow_back)
                }.clicks handledBy {
                    GlobalServiceMessagesStore.forceClear()
                    contractStore.showList()
                }
            }
        }
        contractDetailsInformation(details)
        contractDetailsParticipants(details)
        contractDetailsDelegateToSpecificLineOfBusinessData(details)
        contractDetailsRateAndDebtCollection(details)
        contractDetailsDocuments(details)
        contractDetailsServiceOptions(details)
    }
    buttonContainer {
        btn {
            label("Zurück zur Übersicht")
            icon(MaterialIconsOutlined.arrow_back)
        }.clicks handledBy {
            GlobalServiceMessagesStore.forceClear()
            contractStore.showList()
        }
    }
}

/**
 * Allgemeine Informationen zum Vertrag
 *
 * @param contractDetail
 */
private fun RenderContext.contractDetailsInformation(contractDetail: ContractDetails) {
    detailsSection {
        detailsGrid {
            detailsGridEntry(
                "Versicherungsnehmer",
                contractDetail.participants.firstOrNull { p ->
                    p.role == PartnerRole.Versicherungsnehmer
                }?.name
            )
            detailsGridEntry("Partnernummer", contractDetail.general.policyholder)
            detailsGridEntry("Vertragsnummer", contractDetail.general.contractNumber)
            detailsGridEntry("Status", contractDetail.general.status.value)
            detailsGridEntry("Beginn", contractDetail.general.begin?.toLocalizedString())
            detailsGridEntry("Ablauf", contractDetail.general.end?.toLocalizedString())
            detailsGridEntry("Tarifdatum", contractDetail.general.tariffDate?.toLocalizedString())
        }
    }
}


/**
 * Anzeige der Vertragsbeteiligten, sobald unterschiedliche Partner beteiligt sind
 *
 * @param contractDetail
 */
private fun RenderContext.contractDetailsParticipants(contractDetail: ContractDetails) {
    if (contractDetail.participants.any { it.partnerId != contractDetail.general.policyholder }) {
        detailsSection("Vertragsbeteiligte") {
            detailsPersonRoleTable {
                contractDetail.participants.groupBy(Participant::partnerId)
                    .mapNotNull { group ->
                        val participant = group.value.first()
                        val roles = group.value.joinToString(", ") { it.role.value }
                        participant to roles
                    }.forEach { (participant, roles) ->
                        detailsPersonRoleTableEntry(
                            participant.name,
                            participant.address.toView(),
                            participant.birthdate?.toLocalizedString(),
                            roles
                        )
                    }
            }
        }
    }
}

/**
 * Anzeige der Spartenspezifischen Inhalte
 *
 * @param contractDetail
 */
private fun RenderContext.contractDetailsDelegateToSpecificLineOfBusinessData(contractDetail: ContractDetails) {
    when (contractDetail.lineOfBusinessData?.lineOfBusiness) {
        LineOfBusiness.Kfz -> contractDetailsCar(
            contractDetail.lineOfBusinessData as Car
        )

        LineOfBusiness.Unfall -> contractDetailsAccident(
            contractDetail.lineOfBusinessData as Accident
        )

        LineOfBusiness.Haftpflicht -> contractDetailsLiability(
            contractDetail.lineOfBusinessData as Liability
        )

        LineOfBusiness.HaftpflichtLegacy -> contractDetailsLiabilityLegacy(
            contractDetail.lineOfBusinessData as LiabilityLegacy
        )

        LineOfBusiness.Sach -> contractDetailsProperty(
            contractDetail,
            contractDetail.lineOfBusinessData as Property
        )

        LineOfBusiness.SachLegacy -> contractDetailsPropertyLegacy(
            contractDetail,
            contractDetail.lineOfBusinessData as PropertyLegacy
        )

        LineOfBusiness.Leben -> contractDetailsLife(
            contractDetail,
            contractDetail.lineOfBusinessData as Life
        )

        LineOfBusiness.Rechtsschutz -> contractDetailsLegalExpense()
        LineOfBusiness.Kranken -> contractDetailsHealth(contractDetail)
        LineOfBusiness.FDLProdukte -> contractDetailsInvestment()
        else -> {}
    }
}

/**
 * Beitrags und Kontoinformationen
 *
 * @param contractDetail
 */
private fun RenderContext.contractDetailsRateAndDebtCollection(contractDetail: ContractDetails) {
    detailsSection("Beitragsinformationen und Kontodaten") {
        detailsGrid {
            if (contractDetail.debtCollection.isDirectDebit) {
                detailsGridEntry(
                    "Kontoinhaber", contractDetail.debtCollection.accountHolderName
                )
                detailsGridEntry("IBAN", contractDetail.debtCollection.iban)
            }
            detailsGridEntry(
                "Beitrag", contractDetail.general.rate.toEuro()
            )
            detailsGridEntry("Zahlweise", contractDetail.general.paymentSchedule.value)
            detailsGridEntry(
                "Zahlungsart",
                if (contractDetail.debtCollection.isDirectDebit) "Lastschrift" else "Überweisung"
            )
            if (contractDetail.debtCollection.isDirectDebit && (contractDetail.debtCollection.transactionType != "UM")) {
                // Umbuchungen zB bei Übertragung von Verträgen im Todesfall sind interne Abbuchungen und werden nicht dargestellt
                detailsGridEntry(
                    "Letzte Abbuchung",
                    contractDetail.debtCollection.transactionDate?.toLocalizedString()
                )
            }
        }
    }
}

/**
 * Anzeige von Vertragsdokumenten
 *
 * @param contractDetail
 */
private fun RenderContext.contractDetailsDocuments(contractDetail: ContractDetails) {
    if (contractDetail.documents.isNotEmpty()) {
        detailsSection("Dokumente") {
            div("mb-2") {
                +"Safari-Benutzer: Bitte erlauben Sie Popups in Ihren Browser-Einstellungen, da sich die Downloads in einem neuen Fenster öffnen."
            }
            div("flex flex-col lg:flex-row gap-2 items-start") {
                contractDetail.documents.forEach { document ->
                    pdfDownloadButton(document)
                }
            }
        }
    }
}

private data class ServiceEntry(
    val title: String,
    val icon: Icon,
    val target: NavigationTarget
) {
    companion object {
        private fun serviceEntryOf(page: Page.Internal): ServiceEntry =
            page.run { ServiceEntry(linkTitle, icon ?: MaterialIconsOutlined.error, target) }

        val carServices: List<ServiceEntry> = buildList {
            add(serviceEntryOf(serviceCarInsuranceDeregister))
            add(serviceEntryOf(serviceCarInsuranceCard))
            add(serviceEntryOf(serviceCarInsuranceDigiCard))
            add(serviceEntryOf(serviceCarTaxCertificates))
        }

        val houseServices: List<ServiceEntry> =
            listOf(serviceEntryOf(myDataContact).copy(title = "Adresse ändern", icon = MaterialIconsOutlined.home))

        val lifeServices: List<ServiceEntry> = listOf(serviceEntryOf(serviceLifeEntryNotification))
    }
}

/**
 * Anzeige von passenden Service-Optionen zu einer Sparte
 *
 * @param contractDetail
 */
private fun RenderContext.contractDetailsServiceOptions(contractDetail: ContractDetails) {
    contractDetail.lineOfBusinessData?.lineOfBusiness.let {
        when (it) {
            LineOfBusiness.Kfz -> ServiceEntry.carServices
            LineOfBusiness.Sach, LineOfBusiness.SachLegacy -> ServiceEntry.houseServices
            LineOfBusiness.Leben -> {
                //Auskommentiert, um die fachlichen Bedingungen nicht zu verlieren. Es gibt derzeit keine allgemeinen
                // Steuerbescheinigungen im Kupo, nur die dunkerlverarbeiteten für KFZ.
//                // Rieserverträge und Basisrenten rausfiltern, da sie keine Steuerbesch. beantragen können
//                if (contractDetail.general.salesProduct != SalesProduct.Praemienrente.value
//                    && contractDetail.general.salesProduct != SalesProduct.Basisrente.value
//                    && contractDetail.general.salesProduct != SalesProduct.BasisrenteAlt.value
//                ) ServiceEntry.lifeServices + ServiceEntry.taxServices else
                ServiceEntry.lifeServices
            }

            else -> emptyList()
        }
    }.apply {
        if (isNotEmpty()) {
            h4("vui-label-3 mt-3") { +"Service-Optionen zu Ihrem Vertrag" }
            div("flex flex-wrap gap-4 items-center") {
                forEach { entry ->
                    button(
                        joinClasses(
                            "p-1 w-24 xl:w-32 group",
                            "flex flex-col gap-2 items-center",
                            "bg-white border border-transparent box-border rounded shadow",
                            "hover:border hover:border-primary-700",
                        )
                    ) {
                        icon("h-12 w-12 mt-3 text-primary-500 group-hover:text-primary-700", icon = entry.icon)
                        span("w-full vui-sublabel-4 overflow-ellipsis overflow-hidden") {
                            +entry.title
                        }
                    }.clicks handledBy { entry.target.visit() }
                }
            }
        }
    }
}

fun RenderContext.clausesSection(clauses: List<PMClause>) {
    clauses.withLastElementFlag().forEach { (clause, isLastElement) ->
        detailsGrid {
            detailsGridEntry("Bezeichnung", clause.clauseType)
            detailsGridEntry("Artbezeichnung", clause.clauseName)
            detailsGridEntry("Indiv. Klauseltext", clause.clauseIndividual)
            detailsGridEntry(
                "Bedingung", {
                    if (clause.termsAndConditions.isNotEmpty() && clause.termsAndConditionsLink.isNotEmpty()) {
                        pdfDownloadButton(clause.termsAndConditionsLink, clause.termsAndConditions)
                        div("text-sm") {
                            +"Safari-Benutzer: Bitte erlauben Sie Popups in Ihren Browser-Einstellungen, da sich die Downloads in einem neuen Fenster öffnen."
                        }
                    } else {
                        span { +"―" }
                    }
                }
            )
        }
        if (!isLastElement) separator()
    }
}